'use client';

import Link from 'next/link';
import { Atom, Users, Zap, LogIn } from 'lucide-react';
import { useAuth } from '@/lib/useAuth';

export default function Home() {
  const { user, isLoading, isAuthenticated } = useAuth();

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 cyber-grid relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background pointer-events-none" />
      <div className="max-w-4xl w-full relative z-10">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <Atom className="h-20 w-20 text-primary animate-spin" style={{ animationDuration: '10s' }} />
              <div className="absolute inset-0 h-20 w-20 bg-primary/20 blur-xl rounded-full" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4 glow-text tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-primary">WORLDLINES</span>
          </h1>
          <p className="text-xl text-muted-foreground tracking-wide">
            The official companion wiki for the Worldlines tabletop roleplaying game
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <span className="px-3 py-1 text-xs bg-accent/20 text-accent rounded-full border border-accent/30">
              Version 2.1
            </span>
            <span className="px-3 py-1 text-xs bg-secondary/20 text-secondary rounded-full border border-secondary/30">
              Sci-Fi TTRPG
            </span>
          </div>
          {!isLoading && isAuthenticated && user && (
            <p className="mt-4 text-sm text-muted-foreground">
              Welcome back, {user.firstName || user.email || 'Traveler'}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <Link
            href="/wiki/home"
            className="group flex flex-col items-center text-center p-8 rounded-xl glass-panel hover:border-primary/50 transition-all hover:glow-border"
          >
            <div className="p-4 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors mb-5">
              <Zap className="h-10 w-10" />
            </div>
            <h2 className="text-2xl font-semibold text-primary mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>Wiki</h2>
            <p className="text-muted-foreground leading-relaxed">
              Browse the complete game rules, character creation guides, combat mechanics, and more.
            </p>
          </Link>

          {isAuthenticated ? (
            <Link
              href="/characters"
              className="group flex flex-col items-center text-center p-8 rounded-xl glass-panel hover:border-secondary/50 transition-all hover:glow-border"
            >
              <div className="p-4 rounded-xl bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors mb-5">
                <Users className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-semibold text-secondary mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>Characters</h2>
              <p className="text-muted-foreground leading-relaxed">
                Create and manage your Worldlines characters with digital character sheets.
              </p>
            </Link>
          ) : (
            <a
              href="/api/login"
              className="group flex flex-col items-center text-center p-8 rounded-xl glass-panel hover:border-secondary/50 transition-all hover:glow-border"
            >
              <div className="p-4 rounded-xl bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors mb-5">
                <LogIn className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-semibold text-secondary mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>Sign In</h2>
              <p className="text-muted-foreground leading-relaxed">
                Join the Worldlines community now and build your character and manage your campaign.
              </p>
            </a>
          )}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground font-mono">
            <span className="text-primary">&gt;</span> Worldlines is a TTRPG created by Bryan Todtenhoefer. &copy; Bryan Todtenhoefer. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
